import { NOTIFICATION_TITLES } from '@nexus/core';
import { adminEn } from './admin.en';
import { catalogsEn } from './catalogs.en';
import { documentsEn } from './documents.en';
import { findingsEn } from './findings.en';
import { plansEn } from './plans.en';
import { questionnairesEn } from './questionnaires.en';
import { signatureEn } from './signature.en';
import type { Messages } from './de';

export type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const en: Shape<Messages> = {
  app: { name: 'STAW App' },
  ui: {
    close: 'Close',
    loading: 'Loading',
    moreActions: 'More actions',
    noResults: 'No results',
    required: 'Required',
    optional: 'optional',
    clear: 'Clear',
    selectAll: 'Select all',
    selectRow: 'Select row',
    sortAscending: 'Sort ascending',
    sortDescending: 'Sort descending',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    retry: 'Try again',
    confirm: 'Confirm',
    cancel: 'Cancel',
    breadcrumbs: 'Path',
    filters: 'Filters',
    resetFilters: 'Reset filters',
    steps: 'Steps',
    stepCompleted: 'done',
    back: 'Back',
    next: 'Next',
  },
  common: {
    back: 'Back',
    erasedPerson: 'Deleted person',
    save: 'Save',
    saved: 'Saved',
    reset: 'Reset',
    signOut: 'Sign out',
    cancel: 'Cancel',
    create: 'Create',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
    actions: 'Actions',
    saveFailed: 'Not saved. Please try again.',
    loadFailed: 'Could not be loaded. Check the connection and try again.',
    relative: {
      justNow: 'just now',
    },
  },
  shell: {
    skipToContent: 'Skip to content',
    navigation: 'Main navigation',
    home: 'Home',
    menu: 'Menu',
    bottomBar: 'Quick access',
    collapseSidebar: 'Collapse navigation',
    expandSidebar: 'Expand navigation',
    expandGroup: 'Show pages of “{{name}}”',
    collapseGroup: 'Hide pages of “{{name}}”',
    modules: 'Modules',
    admin: 'Administration',
    help: 'Guides',
    forMe: 'For me',
    forMeCount_one: '{{count}} waiting',
    forMeCount_other: '{{count}} waiting',
    moduleSettings: 'Settings',
    refreshed: 'Updated',
    pushPrompt: {
      title: 'Allow notifications?',
      description: 'The STAW App reports new notifications on this device, even when it is closed.',
      hint: 'The notice only states the number of new notifications, never their content. This can be changed in the profile at any time.',
      allow: 'Allow',
      later: 'Later',
    },
    update: {
      available: 'A new version of the STAW App is available. The next page change loads it.',
      reload: 'Reload',
      later: 'Later',
    },
    search: {
      open: 'Search',
      label: 'Search',
      placeholder: 'People, records, pages …',
      empty: 'Nothing found.',
      pages: 'Pages',
      settings: 'Settings',
      actions: 'Actions',
      navigate: 'select',
      select: 'open',
      shortcut: 'Ctrl K',
      dismiss: 'close',
      loading: 'Searching …',
      recent: 'Recently opened',
      guides: 'Guides',
      showAll: 'All hits for “{{term}}”',
      kinds: { account: 'People', group: 'Groups', core: 'Administration' },
    },
    company: {
      switch: 'Switch company',
      title: 'Company',
      active: 'Active',
      switchFailed: 'Could not switch the company. Please try again.',
      noOther: 'No other company linked.',
    },
    sync: {
      title: 'Sync',
      states: {
        synced: 'Synced',
        syncing: 'Syncing …',
        offline: 'Offline',
        pending_one: '{{count}} change waiting',
        pending_other: '{{count}} changes waiting',
        attention: 'Needs review',
      },
      connection: 'Connection',
      online: 'Online',
      offline: 'No connection',
      lastSync: 'Last sync',
      never: 'not yet',
      pending: 'Pending changes',
      pendingNone: 'No pending changes.',
      rejected: 'Rejected changes',
      syncNow: 'Sync now',
      offlineData: 'Available offline',
      offlineDataNone: 'No data yet. Modules decide what is available offline.',
      pendingCount_one: '{{count}} change is waiting to be sent.',
      pendingCount_other: '{{count}} changes are waiting to be sent.',
      recordCount_one: '{{count}} entry',
      recordCount_other: '{{count}} entries',
      notSynced: 'Not synced',
      conflict: 'Changed on the server in the meantime',
      compare: 'Compare',
      discard: 'Discard',
      compareTitle: 'Compare change',
      compareDescription:
        'The entry was changed on the server while this device was offline. Which version should apply?',
      deletedOnServer:
        'The entry was deleted on the server while this device was offline. The change can no longer be sent.',
      field: 'Field',
      mine: 'On this device',
      server: 'On the server',
      differs: 'differs',
      keepMine: 'Keep my change',
      keepServer: 'Keep server version',
      keptMine: 'The change will be sent again.',
      keptServer: 'The server version applies.',
      signOutPendingTitle: 'Changes not sent',
      signOutPending_one:
        '{{count}} change or file on this device has not reached the server yet. Signing out deletes it.',
      signOutPending_other:
        '{{count}} changes or files on this device have not reached the server yet. Signing out deletes them.',
      signOutAnyway: 'Sign out anyway',
      uploads: 'Files to upload',
      uploadWaiting: 'Waiting for upload',
      uploadWaitingBecause: 'Waiting: {{reason}}',
      uploading: 'Uploading …',
      retryUpload: 'Send again',
      device: 'On this device',
      files: 'Files',
      fileCount_one: '{{count}} file, {{size}}',
      fileCount_other: '{{count}} files, {{size}}',
      filesNone: 'None',
      prepared: 'Prepared',
      storage: 'Storage',
      storageUsed: '{{used}} of {{quota}} used',
      storageFull:
        'The storage of the device is full. Not all files of prepared entries are available offline.',
      clear: 'Delete offline data',
      clearTitle: 'Delete offline data from this device?',
      clearDescription:
        'All entries, files and preparations of this company are deleted from this device. Everything stays on the server. The app fetches catalogs and templates again with a connection, prepared entries only once you prepare them again. Without a connection the app starts again only after the next sync.',
      clearUnsent_one: '{{count}} change or file has not reached the server yet and will be lost.',
      clearUnsent_other:
        '{{count}} changes or files have not reached the server yet and will be lost.',
      clearWord: 'DELETE',
      clearWordLabel: 'Type DELETE to confirm',
      clearConfirm: 'Delete',
      cleared: 'Offline data deleted',
      clearFailed: 'Deleting failed. Please try again.',
      kernel: {
        files: 'File lists',
        findings: 'Findings',
        comments: 'Comments',
        people: 'People directory',
        catalogs: 'Catalogs (e.g. defect catalog)',
        plans: 'Plans',
        questionnaireTemplates: 'Questionnaire templates',
        signature: 'Own signature',
        other: 'Further data',
      },
      prepare: {
        action: 'Make available offline',
        preparing: 'Preparing …',
        progress: '{{done}} of {{total}} files on the device',
        needsConnection: 'Preparing needs a connection.',
        ready: 'Available offline',
        incomplete: 'Available offline, {{done}} of {{total}} files',
        waiting: 'Not on this device yet, comes with the next sync',
        retry: 'Try again',
        details: 'As of {{when}}, {{size}}',
        detailsNoFiles: 'As of {{when}}',
        auto: 'Prepared automatically',
        keep: 'Keep offline',
        kept: 'Stays available offline until you remove it',
        remove: 'Remove files from device',
        removeTitle: 'Remove files from device?',
        removeDescription:
          'The files of this entry are deleted from this device and are no longer available without a connection. Everything stays on the server.',
        removeConfirm: 'Remove',
        removed: 'Files removed from device',
        failed: 'Preparing failed. Please try again with a connection.',
      },
      reasons: {
        forbidden: 'No permission (any more)',
        'not-found': 'Entry not found',
        exists: 'Entry already exists',
        'too-old': 'Offline for too long, the change expired',
        'invalid-time': 'The clock of the device is wrong',
        'command-reused': 'Already sent with other content',
        expired: 'Offline data expired, content deleted from the device',
        invalid: 'Invalid input',
        'file-too-large': 'File too large',
        'file-type-not-allowed': 'File type not allowed here',
        'file-content-mismatch': 'Content does not match the file extension',
        'file-infected': 'Malware found',
        'file-exists': 'File already exists with other content',
        'file-name-invalid': 'Invalid file name',
        'file-missing': 'File on the device no longer readable',
        'scanner-unavailable': 'Virus scan not reachable right now',
        'files-unavailable': 'File storage not reachable right now',
        'finding-catalog-invalid': 'Catalog entry not available (any more)',
        'finding-plan-invalid': 'Plan not available (any more)',
        'finding-photo-invalid': 'Photo does not belong to this finding',
        'finding-source-invalid': 'Finding taken over not found',
        'finding-too-large': 'Too many or too long photo captions',
        'finding-exists': 'Finding already exists',
        'too-many-findings': 'Maximum number of findings for this entry reached',
        'server-error': 'Server not reachable right now',
        other: 'Refused by the server',
      },
    },
    notifications: {
      title: 'Notifications',
      open: 'Notifications',
      openUnread_one: 'Notifications, {{count}} unread',
      openUnread_other: 'Notifications, {{count}} unread',
      markAllRead: 'Mark all read',
      emptyTitle: 'All caught up',
      emptyDescription: 'New notifications appear here.',
      unread: 'Unread',
    },
    profile: {
      open: 'Profile and settings',
      myProfile: 'Profile and settings',
      theme: 'Appearance',
      language: 'Language',
      signOutFailed: 'Could not sign out. Please try again.',
    },
    mfaBanner: 'Signed in without MFA. Administrative permissions are locked in this session.',
    mfaBannerPassword:
      'Administrative permissions need a sign-in with a passkey or with an authenticator app as second step.',
    mfaBannerPasskey:
      'This session was signed in with the password only, so administrative permissions are locked. To unlock them, sign out and sign in again with your passkey.',
    mfaSetUp: 'Set up',
    mfaSignInAgain: 'Sign in again',
    sessionEnded: 'Session expired. Please sign in again.',
  },
  login: {
    title: 'Sign in',
    chooseCompany: 'Choose company',
    chooseCompanyHint: 'Sign in with the account of your company.',
    otherCompany: 'Choose another company',
    redirecting: 'Redirecting to the sign-in of {{company}} …',
    noCompanies: 'No company available. Please contact IT.',
    footer: 'Internal application of the STAW group',
    withSso: 'Sign in with company account (SSO)',
    withPasskey: 'Sign in with a passkey',
    or: 'or',
    email: 'Email address',
    emailInvalid: 'Please enter a complete email address.',
    password: 'Password',
    signIn: 'Sign in',
    forgot: 'Forgot password?',
    forgotTitle: 'Forgot password',
    forgotHint: 'A link to set a new password goes to the email address of the account.',
    sendLink: 'Send link',
    forgotSent:
      'If the address belongs to an account with a password, an email with a link is on its way. The link works for one hour.',
    backToLogin: 'Back to sign-in',
    passwordSet: 'The password is saved. Sign in with it now.',
    twoFactor: {
      totp: {
        title: 'Code of the authenticator app',
        hint: 'The app shows a new code for the STAW App every 30 seconds.',
        code: 'Six-digit code',
        invalid: 'Enter the six digits from the app.',
      },
      backup: {
        title: 'Backup code',
        hint: 'One of the codes saved when the app was set up. Each works once.',
        code: 'Backup code',
      },
      useBackup: 'Use a backup code',
      useApp: 'Use the code of the app',
      usePasskey: 'Use a passkey',
      confirm: 'Confirm',
    },
    passkeyStep: {
      title: 'Confirm sign-in with passkey',
      hint: 'This account has a passkey set up as second step. Your browser or password manager now asks for it.',
      confirm: 'Confirm with passkey',
      unsupported:
        'This browser cannot use passkeys. Please use another browser or device; if the passkeys are lost, the administrators of the company help.',
    },
    errors: {
      'account-locked': 'This account is locked. Please contact the IT of the company.',
      cancelled: 'Sign-in cancelled.',
      'company-unavailable': 'This company is not available.',
      'flow-expired': 'Sign-in took too long. Please sign in again.',
      'mfa-required': 'This account needs a sign-in with MFA. Please sign in again with MFA.',
      'provider-error': 'Sign-in with the company failed. Please try again.',
      'provider-unavailable':
        'The sign-in service of the company does not respond. Please try again later.',
      'rate-limited': 'Too many sign-in attempts. Please wait a minute.',
      'sso-unavailable': 'Sign-in with SSO is not set up for this company.',
      unknown: 'Sign-in failed. Please try again.',
    },
  },
  password: {
    new: 'New password',
    repeat: 'Repeat new password',
    rules:
      'At least {{min}} characters. A sentence of several words is easy to remember and hard to guess.',
    tooShort: 'At least {{min}} characters.',
    tooLong: 'At most {{max}} characters.',
    mismatch: 'The two entries do not match.',
    link: {
      invite: {
        title: 'Set password',
        hint: 'The new account signs in to the STAW App with it.',
        submit: 'Set password',
        expired:
          'The invitation has expired or was used already. Please ask the administrators of the company for a new one.',
      },
      reset: {
        title: 'Set a new password',
        hint: 'Afterwards all devices are signed out, signing in works with the new password.',
        submit: 'Save password',
      },
      incomplete:
        'The link is incomplete. Please open the whole link from the email or copy it into the address bar.',
      toLogin: 'Go to sign-in',
    },
    errors: {
      'account-locked': 'This account is locked. Please contact the administrators of the company.',
      'company-unavailable': 'This company is not available.',
      'invalid-code': 'The code is not right. Please enter the current code.',
      'invalid-credentials': 'Email address or password are not right.',
      'invalid-password': 'The password is not right.',
      'invalid-token':
        'The link has expired or was used already. "Forgot password?" on the sign-in page sends a new one.',
      'mfa-required':
        'Sign in with a passkey or with password and authenticator app first. If both are lost, the administrators of the company help.',
      'no-password': 'No password is set for this account.',
      'passkey-failed': 'The passkey was not accepted. Please try again.',
      'passkey-required': 'This account needs a sign-in with passkey as second step.',
      'passkey-unknown':
        'This passkey is not stored in the STAW App, maybe it was removed. Please sign in with your password.',
      'password-login-disabled': 'This company signs in with SSO only.',
      'password-too-long': 'The password is too long.',
      'password-too-short': 'The password is too short.',
      'rate-limited': 'Too many attempts. Please try again later.',
      'two-factor-expired': 'Sign-in took too long. Please sign in with the password again.',
      'two-factor-locked': 'Too many wrong codes. Confirming is locked for 15 minutes.',
      unknown: 'Failed. Please try again, otherwise tell the IT of the company.',
    },
  },
  home: {
    title: 'Overview',
    customize: 'Customize',
    customizeTitle: 'Customize overview',
    customizeDescription:
      'Show or hide tiles and change their order. Applies to your account only.',
    general: 'General',
    moveUp: 'Move {{title}} up',
    moveDown: 'Move {{title}} down',
    restoreDefault: 'Restore default',
    saveCompanyDefault: 'Use as company default',
    removeCompanyDefault: 'Remove company default',
    companyDefaultHint:
      'The company default applies to everyone who has not customized their overview.',
    allHiddenTitle: 'All tiles are hidden',
    allHiddenDescription: 'Use "Customize" to show tiles again.',
    tileFailed: 'Tile could not be loaded',
    tiles: {
      reminders: {
        title: 'My reminders',
        description: 'Open reminders, earliest first',
        emptyDescription: 'Reminders are set on the page of an entry.',
      },
      approvals: {
        title: 'My approvals',
        description: 'Entries waiting for a decision',
        waiting_one: '{{count}} entry waiting',
        waiting_other: '{{count}} entries waiting',
        waitingMore: 'More than {{count}} entries waiting',
        emptyTitle: 'Nothing to decide',
        emptyDescription: 'New requests appear here and as a notification.',
      },
      recent: {
        title: 'Recently opened',
        description: 'Entries opened recently, stored in this browser only',
        emptyTitle: 'Nothing opened yet',
        emptyDescription:
          'Projects, appointments, customers and other entries you open appear here.',
      },
      whatsNew: {
        title: 'What’s new',
        description: 'The latest changes of the app',
      },
    },
  },
  whatsNew: {
    title: 'What’s new',
    description: 'Changes in the STAW App, newest first.',
    howTo: 'How to',
    tryIt: 'Try it',
    emptyTitle: 'Nothing new yet',
    emptyDescription: 'This is where changes to the app and how to use them will appear.',
  },
  search: {
    title: 'Search',
    description: 'Searches everything that may be opened in the active company.',
    term: 'Search term',
    origin: 'Area',
    allOrigins: 'All areas',
    startTitle: 'Enter a search term',
    startDescription: 'From two characters. Accents and small typos do not matter.',
    emptyTitle: 'Nothing found',
    emptyDescription: 'Try another term or all areas.',
    results_one: '{{count}} hit',
    results_other: '{{count}} hits',
  },
  files: {
    title: 'Files',
    count_one: '{{count}} file',
    count_other: '{{count}} files',
    hint: 'Up to {{size}} per file.',
    add: 'Add file',
    empty: 'No files yet',
    emptyHint: 'Drop files here or choose them with “Add file”.',
    uploading: 'Uploading and checking …',
    added: '“{{name}}” added',
    removed: '“{{name}}” removed',
    remove: 'Remove “{{name}}”',
    removeTitle: 'Remove “{{name}}”?',
    removeDescription: 'The file will be deleted for good.',
    removeConfirm: 'Remove',
    removeAll: 'Remove all',
    removeAllTitle_one: 'Remove {{count}} file?',
    removeAllTitle_other: 'Remove all {{count}} files?',
    removeAllDescription: 'The files will be deleted for good.',
    removeAllConfirm: 'Remove all',
    removedAll_one: '{{count}} file removed',
    removedAll_other: '{{count}} files removed',
    system: 'by the system',
    otherCompany: 'other company',
    unscanned: 'not scanned',
    offlineHint: 'No connection: as of the last sync.',
    offlineUnavailable: 'Not available without a connection',
    queued: '“{{name}}” will be uploaded once there is a connection.',
    waiting: 'Waiting for upload',
    waitingBecause: 'Waiting: {{reason}}',
    uploadRefused: 'Refused: {{reason}}',
    retryUpload: 'Send “{{name}}” again',
    discardUpload: 'Discard “{{name}}”',
    expired: 'Expired file',
    notOnDevice: '“{{name}}” is not stored on this device.',
    downloadZip: 'Download all as ZIP',
    errors: {
      'files-zip-too-large':
        'Together the files are too large for one ZIP archive. Please choose fewer files or download them one by one.',
      zipFailed: 'The ZIP archive could not be downloaded. Please try again.',
      failed: '“{{name}}” could not be uploaded.',
      removeFailed: '“{{name}}” could not be removed.',
      'file-type-not-allowed': '“{{name}}”: this file type is not allowed here.',
      'file-too-large': '“{{name}}” is larger than {{size}}.',
      'file-content-mismatch': '“{{name}}”: the content does not match the file extension.',
      'file-infected': '“{{name}}” contains malware and was rejected.',
      'scanner-unavailable': 'The virus scan is not reachable right now, please try again later.',
      'files-unavailable': 'The file storage is not reachable right now.',
      offline: '“{{name}}”: nothing can be uploaded here without a connection.',
      'device-storage-full': 'There is no room left on this device for “{{name}}”.',
      'image-unreadable':
        '“{{name}}” cannot be read as an image. Please save it as JPEG or PNG; for iPhone photos choose e.g. Settings › Camera › Formats “Most Compatible”.',
    },
  },
  documentStore: {
    title: 'Documents',
    noFolder: 'No folder in SharePoint yet',
    empty: 'The folder is empty',
    emptyHint: 'Upload files with “Add file” or put them into SharePoint directly.',
    openFolder: 'Open in SharePoint',
    openItem: 'Open “{{name}}” in SharePoint',
    link: 'Link file',
    linked: 'linked',
    linkedToast: '“{{name}}” linked',
    linkTitle: 'Link a SharePoint file',
    linkDescription:
      'The file stays where it is and also shows up here. Only files from the library of the company.',
    linkUrl: 'Link to the file',
    linkInvalid: 'Please enter a link from SharePoint that starts with https://.',
    unlink: 'Remove link to “{{name}}”',
    unlinkTitle: 'Remove link to “{{name}}”?',
    unlinkDescription: 'The file stays in SharePoint.',
    unlinked: 'Link to “{{name}}” removed',
    removeDescription: 'The file goes to the recycle bin of the SharePoint site.',
    removeAllDescription:
      'The files go to the recycle bin of the SharePoint site, linked files stay in SharePoint. Subfolders stay.',
    missing: 'File no longer in SharePoint',
    errors: {
      'document-conflict': 'A file with this name was just changed. Please try again.',
      'document-folder-missing': 'This entry has no folder in SharePoint yet.',
      'document-not-a-file': 'The link leads to a folder, not to a file.',
      'document-not-found': 'The file is no longer in SharePoint.',
      'document-outside-library':
        'The file is not in the SharePoint library of the company and cannot be linked.',
      'sharepoint-denied':
        'SharePoint refused access. The administration needs to check the connection.',
      'sharepoint-disabled': 'Storing documents in SharePoint is switched off.',
      'sharepoint-unavailable': 'SharePoint cannot be reached right now. Please try again later.',
    },
  },
  customFields: {
    title: 'More details',
    none: 'No choice',
    yes: 'Yes',
    no: 'No',
    conflict: 'The details were changed in the meantime. The current version is loaded.',
    errors: {
      required: 'Required',
      invalid: 'Invalid value',
      'too-long': 'Too long',
      'unknown-option': 'This choice no longer exists',
    },
  },
  ai: { generated: 'Written by AI' },
  sharing: {
    title: 'Shares',
    description: 'Other companies that may read this entry.',
    share: 'Share',
    none: 'Only this company sees the entry.',
    until: 'until {{date}}',
    unlimited: 'no end date',
    from: 'Shared by {{company}}',
    readOnly: 'read-only',
    end: 'End share with {{company}}',
    endTitle: 'End share with {{company}}?',
    endDescription: 'The company no longer sees the entry, right away.',
    endConfirm: 'End',
    ended: 'Share with {{company}} ended',
    shared: 'Shared with {{company}}',
    dialogTitle: 'Share entry',
    dialogDescription: 'Another company may read the entry, but not change it.',
    company: 'Company',
    chooseCompany: 'Choose a company',
    noCompanies: 'No other company uses this module.',
    alsoShare: 'Also share',
    expiry: 'Share ends on',
    expiryHint: 'Without a date the share lasts until someone ends it.',
    preview: 'The company sees',
    previewRecord: 'the entry with all its details',
    gdprHint:
      'If the entry holds personal data, the companies need an agreement. When in doubt, ask data protection.',
    badge: 'from {{company}}',
    badgeUnknown: 'shared',
    parts: {
      files: 'Files',
      activity: 'Comments',
      customFields: 'Further details',
      workflows: 'Status',
    },
    errors: { exists: 'The company sees the entry already.' },
  },
  workflow: {
    title: 'Status',
    moved: 'New status: {{state}}',
    reason: 'Reason',
    reasonHint: 'Visible to everyone who can see the record.',
    otherCompany: 'other company',
    system: 'System',
    errors: {
      ownRequest: 'Someone else decides on a request of your own.',
      changed: 'The status has changed in the meantime. The current version is loaded.',
    },
  },
  forMe: {
    title: 'For me',
    description:
      'Approvals, tasks and reminders from all modules. Handle them on the page of the entry.',
    now_one: '{{count}} entry needs action now',
    now_other: '{{count}} entries need action now',
    nothingNow: 'Nothing is waiting right now',
    approvals: 'To decide',
    tasks: 'My tasks',
    due: 'Due reminders',
    coming: 'Upcoming reminders',
    emptyTitle: 'All done',
    emptyDescription:
      'Approvals, tasks and reminders show up here as soon as they are due. Tasks and reminders are set on the page of an entry under "History".',
  },
  approvals: {
    untitled: 'Untitled record',
    requestedBy: 'by {{name}}',
    more: 'More requests are waiting. The oldest 100 are shown.',
  },
  reminders: {
    title: 'Reminders',
    add: 'Reminder',
    none: 'No reminder for this entry.',
    addTitle: 'Set a reminder',
    editTitle: 'Change reminder',
    dialogDescription:
      'A notification arrives on the chosen day. Only the person who set a reminder sees it.',
    dueOn: 'Due on',
    dueOnInvalid: 'Please choose a date.',
    quick: { week: 'In 1 week', month: 'In 1 month', quarter: 'In 3 months' },
    note: 'Note',
    noteHint: 'Briefly what it is about, e.g. "follow up on the offer".',
    noteTooLong: 'At most {{max}} characters.',
    saved: 'Reminder saved',
    finish: 'Reminder of {{day}} done',
    reopen: 'Reopen reminder of {{day}}',
    overdue: 'Overdue',
    edit: 'Change reminder',
    remove: 'Delete reminder',
    removeTitle: 'Delete reminder?',
    removeDescription: 'The reminder and its note are removed.',
    removeConfirm: 'Delete',
    emptyTitle: 'No open reminders',
    untitled: 'Entry without title',
    more: 'There are more reminders. The earliest 100 are shown.',
    errors: {
      conflict: 'The reminder has changed in the meantime. Please reload.',
      tooMany: 'Too many open reminders. Please tick off done ones first.',
    },
  },
  tasks: {
    title: 'Tasks',
    add: 'Task',
    none: 'No open task for this entry.',
    addTitle: 'Add task',
    editTitle: 'Change task',
    dialogDescription:
      'The person responsible gets a notification, and another on the due day. Everyone who may work on tasks here sees them.',
    fields: {
      title: 'What needs doing?',
      description: 'Description',
      assignee: 'Responsible',
      dueOn: 'Due on',
    },
    titleMissing: 'Please describe briefly what needs doing.',
    titleTooLong: 'At most {{max}} characters.',
    descriptionTooLong: 'At most {{max}} characters.',
    assigneeMissing: 'Please choose a person.',
    saved: 'Task saved',
    finish: 'Mark "{{title}}" as done',
    reopen: 'Reopen "{{title}}"',
    overdue: 'Overdue',
    due: 'due {{day}}',
    edit: 'Change task',
    remove: 'Delete task',
    removeTitle: 'Delete task?',
    removeDescription: 'The task and its description will be removed.',
    removeConfirm: 'Delete',
    untitled: 'Untitled entry',
    more: 'There are more tasks. The earliest 100 are shown.',
    errors: {
      assignee: 'This person cannot see the entry or its tasks.',
      conflict: 'The task was changed in the meantime. Please reload.',
      tooMany: 'Too many open tasks on this entry. Please tick off finished ones first.',
    },
  },
  recordSections: {
    failed: 'Section could not be loaded',
    label: 'Sections',
    overview: 'Overview',
    files: 'Files',
    history: 'History',
  },
  activity: {
    title: 'History',
    description: 'Comments and changes, newest first.',
    placeholder: 'Write a comment …',
    label: 'Comment',
    send: 'Send',
    sendHint: 'Ctrl+Enter sends, @ mentions a person.',
    mentionList: 'People to mention',
    empty: 'No entries yet',
    emptyHint: 'Comments and changes appear here.',
    more: 'Load older',
    edited: 'edited',
    edit: 'Edit comment',
    save: 'Save',
    cancel: 'Cancel',
    remove: 'Delete comment',
    removeTitle: 'Delete comment?',
    removeDescription: 'The text is removed for good.',
    removeConfirm: 'Delete',
    system: 'System',
    otherCompany: 'other company',
    platform: 'Platform administration',
    unknownAction: 'Change ({{action}})',
    offline: 'recorded offline',
    offlineNote: 'Without a connection the history shows only the comments on this device.',
    pending: 'waiting for sync',
    unavailable: 'Not available without a connection.',
    tooLong: 'At most {{max}} characters.',
    errors: {
      sendFailed: 'The comment could not be sent.',
      saveFailed: 'The change could not be saved.',
      conflict: 'The comment was changed in the meantime. Please reload.',
      removeFailed: 'The comment could not be deleted.',
    },
  },
  geo: {
    placeholder: 'Search address',
    searchPlaceholder: 'Enter street, place or company',
    typeMore: 'Enter at least two characters',
    noResults: 'No address found',
    failed: 'Address search is not reachable right now',
    unavailable: 'Address search is not set up',
    mapUnavailable: 'Map is not set up',
    attribution: '© OpenStreetMap contributors',
    picker: {
      title: 'Point on the map',
      hint: 'For places without a fitting address, e.g. a construction site: choose the point on the map, enter coordinates or take your own location.',
      map: 'Map to set the point',
      marker: 'Chosen point, drag to move',
      coordinates: 'Coordinates',
      coordinatesHint: 'Latitude, longitude, e.g. from Google Maps or a GPS app',
      invalid: 'Please enter latitude and longitude, e.g. 52.520008, 13.404954.',
      openMap: 'Choose on the map',
      closeMap: 'Close map',
      mapHint: 'Tap the map or drag the marker.',
      locate: 'My location',
      locateDenied: 'The browser may not read the location. Please allow it in the settings.',
      locateFailed: 'The location could not be determined right now.',
      clear: 'Remove point',
    },
    controls: {
      marker: 'Marker',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      attribution: 'Show attribution',
      windowsHelp: 'Use Ctrl and the scroll wheel to zoom',
      macHelp: 'Use ⌘ and the scroll wheel to zoom',
      mobileHelp: 'Use two fingers to move the map',
    },
  },
  vat: {
    check: 'Check VAT ID',
    valid: 'Valid',
    invalid: 'Not assigned',
    unavailable: 'VIES not reachable right now',
    failed: 'Check failed',
    rateLimited: 'Too many checks, please try again shortly',
    checkedAt: 'checked {{when}}',
    problems: {
      empty: 'No VAT ID entered',
      noCountry: 'Country code missing, e.g. DE',
      notEu: 'Not an EU VAT ID',
      format: 'Format does not match the country',
    },
  },
  imports: {
    open: 'Import',
    title: 'Import {{what}}',
    description: 'From a CSV file, e.g. from Excel with “Save as › CSV”.',
    steps: { file: 'File', columns: 'Columns', check: 'Check' },
    fileHint:
      'The first line holds the column names. The file stays on the device, only the assigned values are sent.',
    chooseFile: 'Choose CSV file',
    fileErrors: {
      empty: 'The file has no rows below the column names.',
      'too-many-rows': 'The file has more than 10,000 rows. Please split it.',
      'too-many-columns': 'The file has more than 60 columns.',
      'unclosed-quote': 'The file is damaged: a quotation mark is not closed.',
      unreadable: 'The file cannot be read.',
    },
    columnsHint_one: '{{file}}: {{count}} row. Assign a column to each field.',
    columnsHint_other: '{{file}}: {{count}} rows. Assign a column to each field.',
    notMapped: 'Do not import',
    column: 'Column {{number}}',
    example: 'First row: {{value}}',
    checkRows: 'Check rows',
    checking: 'Checking {{done}} of {{total}} rows …',
    importing: 'Importing {{done}} of {{total}} rows …',
    counts: { new: 'New', duplicate: 'Already there', invalid: 'Will be skipped' },
    duplicates: 'Existing entries',
    duplicatesHint: 'Update overwrites the assigned fields with the values from the file.',
    skip: 'Skip',
    update: 'Update',
    attention: 'Rows with notes',
    line: 'Row',
    status: 'Status',
    details: 'Note',
    statuses: {
      invalid: 'Invalid',
      duplicate: 'Existing',
      repeat: 'Repeated in the file',
      new: 'New',
    },
    repeatOf: 'Same as row {{line}}',
    more_one: 'And {{count}} more row.',
    more_other: 'And {{count}} more rows.',
    row: 'Row',
    errors: {
      required: 'missing',
      too_long: 'too long',
      too_short: 'too short',
      invalid: 'invalid',
    },
    run_one: 'Import {{count}} row',
    run_other: 'Import {{count}} rows',
    failed: 'The import stopped: {{message}}',
    unavailable: 'Importing is not possible right now. Please try again later.',
    partial_one: 'Until then {{count}} row was imported.',
    partial_other: 'Until then {{count}} rows were imported.',
    doneTitle: 'Import finished',
    done: '{{created}} created, {{updated}} updated, {{skipped}} skipped.',
    close: 'Done',
  },
  exports: {
    csv: 'Export',
    xlsxFile: 'Excel file (.xlsx)',
    csvFile: 'CSV file (.csv)',
    vcard: 'Save contact',
  },
  views: {
    trigger: 'Views',
    current: 'View: {{name}}',
    personal: 'My views',
    shared: 'For the whole company',
    none: 'No view saved yet',
    save: 'Save current filters …',
    manage: 'Manage views …',
    saveTitle: 'Save view',
    saveDescription: 'Saves search and filters of this list under a name.',
    name: 'Name',
    share: 'Save for the whole company',
    shareHint: 'Everyone who sees this list finds the view in their selection.',
    saved: 'View saved',
    manageTitle: 'Manage views',
    remove: 'Delete {{name}}',
    removeTitle: 'Delete view?',
    removeDescription: '"{{name}}" will be deleted. The data of the list stays as it is.',
    removeSharedDescription:
      '"{{name}}" will be deleted for the whole company. The data of the list stays as it is.',
    errors: {
      nameTaken: 'A view with this name already exists.',
      tooMany: 'At most {{max}} personal views per list. Please delete an old one.',
      removeFailed: 'The view could not be deleted.',
    },
  },
  help: {
    title: 'Guides',
    description: 'How the STAW App and its modules work.',
    term: 'Search guides',
    area: 'Area',
    allAreas: 'All areas',
    areas: { basics: 'Basics', admin: 'Administration', development: 'Development' },
    button: 'Guide',
    onThisPage: 'On this page',
    results_one: '{{count}} result',
    results_other: '{{count}} results',
    emptyTitle: 'Nothing found',
    emptyDescription: 'Try another term or all areas.',
    noneTitle: 'No guides',
    noneDescription: 'There are no guides for this area yet.',
    api: {
      title: 'API reference',
      description:
        'All endpoints of the kernel and the active modules, with permissions and inputs.',
      download: 'Download OpenAPI',
      filter: 'Path, description or permission',
      empty: 'Try another term or all areas.',
      kernel: 'Kernel',
      count_one: '{{count}} endpoint',
      count_other: '{{count}} endpoints',
      intro:
        'All paths are below `/api`. Calls use the session of the app, i.e. after signing in in the browser. Writing calls also need the CSRF header; name and values are in the OpenAPI file. With an `Idempotency-Key` (UUID), a repeated call takes effect only once. The list shows the endpoints of the active company; only what the permissions allow can be called.',
      public: 'Reachable without sign-in.',
      authenticated: 'Needs a sign-in, no permission.',
      permission: 'Needs the permission {{permission}}.',
      scope: 'Scope at least {{scope}}.',
      parameters: 'Parameters',
      body: 'Body (JSON)',
      responses: 'Responses',
      yes: 'yes',
      no: 'no',
      columns: {
        name: 'Name',
        in: 'In',
        type: 'Type',
        required: 'Required',
        rules: 'Rules',
        field: 'Field',
      },
      in: { path: 'path', query: 'query', header: 'header' },
      rules: { length: 'length', range: 'value', items: 'count', default: 'default' },
      status: {
        '200': 'OK',
        '201': 'Created',
        '204': 'Done, no content',
        '400': 'Invalid input (`invalid-input`)',
        '401': 'Not signed in',
        '403': 'Permission missing',
        '404': 'Not found',
        '409': 'Conflict, e.g. outdated version',
        '429': 'Too many requests, respect `Retry-After`',
      },
    },
    guides: {
      start: {
        title: 'Getting started',
        summary: 'Signing in, switching company, navigation, customizing the overview, start page.',
      },
      search: {
        title: 'Searching',
        summary: 'Find people, records, pages and guides with Ctrl K.',
      },
      files: {
        title: 'Attaching files',
        summary: 'Uploading, allowed file types, virus scan, downloading and removing.',
      },
      activity: {
        title: 'Comments and history',
        summary: 'Commenting, notifications on replies and the changes of an entry.',
      },
      forMe: {
        title: 'For me',
        summary: 'Approvals, tasks and reminders in one place, the number in the navigation.',
      },
      workflows: {
        title: 'Status and approvals',
        summary: 'Steps of a record, reasons and approvals.',
      },
      offline: {
        title: 'Working offline',
        summary:
          'Using data without a connection, sync, conflicts and data protection on the device.',
      },
      ai: {
        title: 'AI functions',
        summary: 'Set up provider and key, choose the data level, functions and usage.',
      },
      sharing: {
        title: 'Shares between companies',
        summary: 'Let other companies read entries, and end shares.',
      },
      privacy: {
        title: 'Data protection: access, erasure, periods',
        summary: 'Export the data of a person, erase people and switch on retention periods.',
      },
      profile: {
        title: 'Profile and notifications',
        summary:
          'Language, appearance, password and authenticator app, signature, alerts about new notifications.',
      },
      accounts: {
        title: 'Accounts and groups',
        summary: 'How accounts are created, invited, locked and added to groups.',
      },
      roles: {
        title: 'Roles and permissions',
        summary: 'Bundle permissions, assign them, set an end date and review them regularly.',
      },
      sso: {
        title: 'Setting up sign-in',
        summary:
          'Connect and test SSO per OpenID Connect or SAML 2.0, allow or end sign-in with a password.',
      },
      integrations: {
        title: 'Connecting SharePoint',
        summary:
          'Keep documents of the modules in a SharePoint library of the company: app registration, access to the site, connection test.',
      },
      notifications: {
        title: 'Setting up notification channels',
        summary: 'Own mail server, Microsoft Teams and push messages for the company.',
      },
      customFields: {
        title: 'Setting up custom fields',
        summary: 'Create, order and delete own fields for module entries.',
      },
      company: {
        title: 'Defaults and appearance',
        summary: 'Settings for everyone in the company, logo and company colour.',
      },
      audit: {
        title: 'Audit log',
        summary: 'See who changed what and when.',
      },
      platform: {
        title: 'Companies, modules and platform permissions',
        summary: 'For superadmins: create companies, switch modules, permissions across companies.',
      },
      operations: {
        title: 'Operations and error IDs',
        summary: 'Read the state of server, database and jobs, find error IDs in the log.',
      },
    },
  },
  profile: {
    title: 'Profile and settings',
    description: 'Applies to this account in every browser and on every device.',
    account: 'Account',
    name: 'Name',
    email: 'Email',
    company: 'Company',
    signInMethod: 'Sign-in',
    methods: { oidc: 'SSO', saml: 'SSO', password: 'Email and password', passkey: 'Passkey' },
    mfa: 'MFA',
    mfaYes: 'Signed in with MFA',
    mfaNo: 'Signed in without MFA',
    security: {
      title: 'Sign-in',
      password: 'Password',
      passwordHint: 'Sign-in with {{email}}.',
      change: 'Change password',
      changeHint: 'Other devices are signed out afterwards.',
      current: 'Current password',
      save: 'Save password',
      changed: 'Password changed',
      app: 'Authenticator app',
      appOff:
        'Second step of the sign-in with a code from an app like Microsoft Authenticator. Needed for administrative permissions.',
      appOn: 'Every sign-in asks for a code of the app.',
      on: 'Set up',
      setUp: 'Set up',
      setupTitle: 'Set up authenticator app',
      confirmPassword: 'Enter the current password to confirm.',
      continue: 'Continue',
      scan: 'Scan the QR code with the app or type the key by hand, then enter the code of the app.',
      qrLabel: 'QR code for the authenticator app',
      openApp: 'Open in the app',
      secret: 'Key',
      copySecret: 'Copy key',
      code: 'Code from the app',
      confirm: 'Confirm',
      enabled: 'Authenticator app set up',
      codesTitle: 'Backup codes',
      codesHint:
        'Each code replaces the code of the app once, e.g. when the phone is missing. Keep them safe now, they are not shown again.',
      codesFile: 'Backup codes of the STAW App, {{company}}',
      copy: 'Copy',
      copied: 'Copied',
      download: 'Download',
      done: 'Done',
      newCodes: 'New backup codes',
      newCodesConfirm: 'The previous codes stop working. Enter the password to confirm.',
      newCodesHint: 'The previous codes no longer work. Keep the new ones safe now.',
      generate: 'Generate',
      remove: 'Remove',
      removeTitle: 'Remove authenticator app?',
      removeHint:
        'Afterwards the password is enough to sign in, administrative permissions need the app again. Other devices are signed out.',
      removed: 'Authenticator app removed',
      passkeys: 'Passkeys',
      passkeysHint:
        'Sign in without a password, confirmed with fingerprint, face, PIN or a password manager such as 1Password. Counts as MFA.',
      needsMfa:
        'Changes need a sign-in with a passkey or authenticator app. If both are lost, the administrators reset the second factor.',
      passkeysUnsupported: 'This browser does not support passkeys.',
      addPasskey: 'Add passkey',
      addPasskeyHint:
        'Enter your current password to confirm. Then the browser or password manager asks where to store the passkey.',
      passkeyAdded: 'Passkey added',
      passkeyKnown: 'A passkey for this account is already stored there.',
      passkeyName: 'Passkey',
      synced: 'Synced',
      passkeyCreated: 'Added on {{date}}',
      passkeyUsed: 'last used on {{date}}',
      passkeyNeverUsed: 'not used yet',
      removePasskey: 'Remove {{name}}',
      removePasskeyTitle: 'Remove passkey “{{name}}”?',
      removePasskeyHint:
        'This passkey no longer signs you in, other devices are signed out. Please delete it in the password manager as well.',
      passkeyRemoved: 'Passkey removed',
    },
    sections: {
      general: 'General',
      appearance: 'Appearance',
      notifications: 'Notifications',
      accessibility: 'Accessibility',
    },
    changed: 'Customised',
    resetToDefault: 'Reset to default',
    channels: {
      always: 'Always',
      app: {
        label: 'In the app',
        description:
          'All notifications appear in the app. The channels below only point to them, without content.',
        onlyChannel:
          'All notifications appear in the app. The company has not set up other channels.',
      },
      email: {
        description: 'A notice to {{address}} when unread notifications are waiting.',
        noAddress: 'No e-mail address is known for this account.',
      },
      push: {
        label: 'Push messages',
        description:
          'Notice in the notification centre of this device when unread notifications are waiting.',
        active: 'On for this device. Notices appear in the notification centre.',
        install:
          'On iPhone and iPad only in the installed app: in Safari use Share › Add to Home Screen, open the STAW App from the home screen and switch it on here.',
        unsupported: 'This browser cannot receive push messages.',
        blocked:
          'Notifications are blocked for the STAW App. On iPhone and iPad allow them under Settings › Notifications › STAW App, on a computer in the site settings of the browser.',
        on: 'On',
        switchOn: 'Switch on for this device',
        switchOff: 'Switch off',
        switchedOn: 'Push messages switched on',
        switchedOff: 'Push messages switched off',
        test: 'Send test',
        testSent: 'Test sent. It arrives within a few seconds.',
        since: 'On since {{date}}',
        devices: {
          iphone: 'iPhone',
          ipad: 'iPad',
          mac: 'Mac',
          windows: 'Windows PC',
          android: 'Android device',
          linux: 'Linux PC',
          other: 'Other device',
        },
        errors: {
          denied:
            'Notifications were declined. They can be allowed again in the settings of the device or browser.',
          'no-worker': 'The app has not finished loading. Please reload the page.',
          'no-device': 'Push messages are not switched on for any device.',
          'too-many-devices': 'Too many devices. Please switch off an old one first.',
          unavailable: 'Push messages are switched off for the company.',
          rejected: 'The push service of the device refused the message.',
          'connection-failed':
            'The push service cannot be reached right now. Please try again later.',
        },
      },
    },
    moduleSettings: 'Module settings',
    moduleSettingsHint: 'More personal settings are found with the modules.',
  },
  moduleSettings: {
    title: 'Settings',
    description: 'Personal settings and, with the permission, the defaults of the company.',
    personal: 'My settings',
    company: 'For the company',
    personalDefaults: 'Defaults for personal settings',
    global: 'For all companies',
    empty: 'Nothing to set here.',
  },
  sequences: {
    title: 'Running numbers',
    description:
      'Numbers new records get in this company. Numbers already given out stay as they are.',
    mode: 'Numbering',
    modes: {
      auto: {
        label: 'Automatic',
        description: 'The STAW App gives the number following the format.',
      },
      manual: {
        label: 'By hand',
        description: 'Whoever creates a record types the number in. The app refuses duplicates.',
      },
    },
    format: 'Format',
    formatHelp:
      '{n} is the number, {n:5} pads it with zeros to five digits, {year} is the year and starts counting again each year, {rand:6} are six random digits (4 to 12).',
    formatInvalid:
      'The format needs {n} or {rand:4} to {rand:12}, each at most once, and may otherwise only contain letters, digits, spaces and . _ / # -.',
    next: 'Next number',
    nextInvalid: 'Please enter a whole number from 1.',
    nextLower: 'Must not be lower than {{current}}, otherwise numbers would repeat.',
    preview: 'The next record gets {{preview}}.',
    previewRandom: 'The next record gets a number like {{preview}}.',
    previewManual: 'The number is typed in when creating a record.',
    number: 'Number',
    numberRequired: 'Please enter a number.',
    numberInvalid:
      'At most 40 characters: letters, digits, spaces and . _ / # -, with at least one letter or digit.',
    numberTaken: 'This number is already taken in the company. Please enter another one.',
    exhausted:
      'No free number was found. Admins can set more digits in the format or a higher next number.',
    changed: 'Customised',
    save: 'Save',
    saved: 'Number sequence saved',
  },
  errors: {
    notFoundTitle: 'Page not found',
    notFoundDescription: 'The address is wrong or the page has moved.',
    forbiddenTitle: 'No access',
    forbiddenDescription:
      'A permission for this page is missing. The administration of the company can grant it.',
    crashTitle: 'Something went wrong',
    crashDescription: 'The page could not be shown. Reloading often helps.',
    reload: 'Reload',
    toHome: 'Go to home',
    serverError: 'Error on the server. Please try again or give the error ID to the support.',
    errorId: 'Error ID: {{id}}',
    crashErrorId: 'Error ID for the support:',
    copyErrorId: 'Copy error ID',
    errorIdCopied: 'Error ID copied',
  },
  core: {
    roles: { people: 'People directory' },
    permissions: {
      people: {
        read: {
          label: 'See people of the company',
          description: 'Name and work email of colleagues, e.g. in pickers.',
        },
      },
    },
    people: {
      placeholder: 'Choose a person',
      search: 'Type a name',
      empty: 'No person found',
      tooShort: 'Type at least two letters',
      offline: 'Not on this device without a connection',
      loading: 'Loading …',
      unavailable: 'not available',
      locked: 'locked',
      add: 'Add person',
      remove: 'Remove {{name}}',
    },
    ...NOTIFICATION_TITLES.en,
    settings: {
      locale: {
        label: 'Language',
        description: 'Language of the interface.',
        options: { de: 'Deutsch', en: 'English' },
      },
      startPage: {
        label: 'Start page',
        description: 'Page that opens after sign-in.',
      },
      dashboard: {
        label: 'Overview tiles',
        description: 'Order and hidden tiles, set on the overview.',
      },
      theme: {
        label: 'Colour scheme',
        description: 'Light, dark or as set in the operating system.',
        options: { system: 'System', light: 'Light', dark: 'Dark' },
      },
      density: {
        label: 'Density',
        description: 'Compact shows more rows in lists and tables.',
        options: { comfortable: 'Normal', compact: 'Compact' },
      },
      emailNotifications: {
        label: 'E-mail',
        description: 'A notice by e-mail when unread notifications are waiting.',
      },
      calendarInvites: {
        label: 'Calendar invitations',
        description:
          'Appointments you are signed up for arrive as e-mail invitations and land in your Outlook calendar. Changes and cancellations follow.',
      },
      teamsNotifications: {
        label: 'Microsoft Teams',
        description: 'A notice in the Teams activity feed.',
      },
      motion: {
        label: 'Motion',
        description: 'Reduce animations or follow the operating system.',
        options: { system: 'System', reduced: 'Reduced' },
      },
    },
  },
  catalogs: catalogsEn,
  findings: findingsEn,
  plans: plansEn,
  documents: documentsEn,
  questionnaires: questionnairesEn,
  signature: signatureEn,
  admin: adminEn,
  company: {
    settings: {
      moduleOrder: {
        label: 'Module order',
        description:
          'Order in the sidebar and on the start page, set under Administration › Modules.',
      },
    },
    roles: {
      'company-admin': 'Company admin',
      auditor: 'Audit',
    },
    permissions: {
      accounts: {
        read: { label: 'View accounts', description: 'List and details of the company accounts.' },
        manage: {
          label: 'Manage accounts',
          description:
            'Create, lock and unlock accounts, send password links, reset the authenticator app.',
        },
      },
      groups: {
        read: { label: 'View groups', description: 'Groups and their members.' },
        manage: {
          label: 'Manage groups',
          description: 'Create, change and delete groups and maintain members.',
        },
      },
      roles: {
        read: {
          label: 'View roles',
          description: 'Roles, assignments and why someone may do something.',
        },
        manage: {
          label: 'Manage roles',
          description: 'Create and change roles, assign and remove them.',
        },
      },
      modules: {
        manage: {
          label: 'Manage modules',
          description: 'Switch the modules of the company on and off.',
        },
      },
      audit: {
        read: { label: 'Read audit log', description: 'Log of all changes in the company.' },
      },
      settings: {
        manage: {
          label: 'Manage settings',
          description: 'Company defaults for everyone.',
        },
      },
      branding: {
        manage: { label: 'Change appearance', description: 'Logo and company colour.' },
      },
      sso: {
        manage: {
          label: 'Set up sign-in',
          description:
            'Set up and test the SSO provider of the company, allow or end sign-in with a password.',
        },
      },
      'custom-fields': {
        manage: {
          label: 'Set up custom fields',
          description: 'Create, change and delete own fields for module entries.',
        },
      },
      shares: {
        manage: {
          label: 'Manage shares',
          description: 'See all shares between companies and end own ones.',
        },
      },
      ai: {
        manage: {
          label: 'Manage AI',
          description:
            'Set the AI provider, own API key, data level, monthly limit and AI functions of the company.',
        },
      },
      findings: {
        manage: {
          label: 'Maintain statuses and priorities of findings',
          description: 'Statuses and priorities that all modules with findings share.',
        },
      },
      privacy: {
        manage: {
          label: 'Manage data protection',
          description: 'Switch on retention periods, export the data of a person and erase people.',
        },
      },
      integrations: {
        manage: {
          label: 'Set up integrations',
          description: 'Connect and test SharePoint as storage for documents.',
        },
      },
      notifications: {
        manage: {
          label: 'Set up notification channels',
          description: 'Set up and test the mail server, Teams and push messages of the company.',
        },
      },
    },
  },
  platform: {
    permissions: {
      superadmin: {
        label: 'Superadmin',
        description: 'All permissions in every company and on the platform.',
      },
      companies: {
        manage: {
          label: 'Manage companies',
          description: 'Create, change and disable companies and subgroups.',
        },
      },
      modules: {
        manage: {
          label: 'Switch modules',
          description: 'Switch modules on for all or single companies.',
        },
      },
      audit: {
        read: { label: 'Read platform audit', description: 'Log of the platform administration.' },
      },
      metrics: {
        read: { label: 'View metrics', description: 'Operating metrics of the platform.' },
      },
      backups: {
        create: {
          label: 'Start backups',
          description: 'Have the server create a database backup right away.',
        },
      },
    },
  },
};
